import React from 'react';
import { Box, Button as MuiButton, Alert, styled } from '@mui/material';
import { spacing } from '@mui/system';
import { datadogRum } from '@datadog/browser-rum';
import PageHeader from './PageHeader';
import { button } from '../constants/button';
import { notifications } from '../constants';

const Button = styled(MuiButton)(spacing);

/**
 * ErrorBoundary component to catch JavaScript errors anywhere in the child component tree
 * and display a fallback UI instead of crashing the whole app.
 */
class ErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  constructor(props) {
    super(props);
    this.logStore = [];
    this.lastEvent = null;

    // Bind methods
    this.handleClickEvents = this.handleClickEvents.bind(this);

    // Setup event listeners and console override
    this.setupErrorTracking();
  }

  setupErrorTracking() {
    // Save console.error to logStore
    const originalError = console.error;
    console.error = (...args) => {
      this.logStore.push({ type: 'error', data: args });
      originalError.apply(console, args);
    };

    // Add click event listener
    document.addEventListener('click', this.handleClickEvents);
  }

  componentDidCatch(error, errorInfo) {
    const errorData = {
      message: error.message,
      stack: errorInfo.componentStack,
      consoles: this.logStore?.slice(0, 10),
      lastEvent: this.lastEvent || 'On Initial Render',
    };

    // Log error to console and Datadog
    console.error('Error caught in ErrorBoundary:', error, errorInfo);
    datadogRum.addError(error.toString(), errorData);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickEvents);
  }

  handleClickEvents(event) {
    this.lastEvent = {
      tag: event.target.tagName,
      className: event.target.className,
      id: event.target.id,
      text: event.target.innerText || event.target.innerHTML,
    };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <PageHeader title={notifications.ERROR.title} />
          <Alert severity="error" data-testid="error-alert" sx={{ mb: 2 }}>
            {notifications.ERROR.MAIL_TEXT}
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={() => window.location.reload()}
              variant="contained"
              color="secondary"
              mt={6}
              data-testid="refresh-errorb-button"
            >
              {button.REFRESH_PAGE}
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
